import { HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Params } from '@angular/router';
import { HttpService } from '@app/core/services/http.service';
import { environment } from '@environments/environment';
import { LIMIT_PER_PAGE, LIMIT_PER_PAGE_DEFAULT } from '@shared/shared.constants';
import { Pagination, VesselItinerary } from '@vessel-itinerary/state/itinerary.types';
import { HfoMgoBunker, Vessel } from '@vessel/vessel/state/vessel.types';
import { map, Observable, of, switchMap, throwError } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ItineraryService {
  public static readonly API: string = `${environment.PROJECT_API}/movement-group/movements`;
  public static readonly ADMIN_API: string = `${environment.PROJECT_API}/admin-group`;

  protected readonly httpService: HttpService = inject(HttpService);

  getVesselsItinerary(queryParams?: Params): Observable<{
    meta: Pagination;
    data: VesselItinerary[];
  }> {
    const params = {
      page: '1',
      limit: LIMIT_PER_PAGE_DEFAULT,
      resource_type: 'simple',
      ...queryParams
    };

    return this.httpService.get<{
      meta: Pagination;
      data: VesselItinerary[];
    }>(`${ItineraryService.API}?`, {
      params: new HttpParams({
        fromObject: params
      })
    });
  }

  getVesselMovements(vessel_id: any, queryParams?: Params): Observable<any> {
    const defaultParams = {
      page: '1',
      limit: LIMIT_PER_PAGE,
      'filter[vessel_id]': vessel_id ?? ''
    };

    return this.httpService
      .get<any>(`${ItineraryService.API}/ports`, {
        params: new HttpParams({ fromObject: Object.keys(queryParams)?.length ? queryParams : defaultParams })
      })
      .pipe(
        map((response) => ({
          pagination: {
            current_page: response?.meta?.current_page,
            last_page: response?.meta?.last_page,
            per_page: response?.meta?.per_page,
            total: response?.meta?.total
          },
          data: response.data
        })),
        switchMap((response) => {
          if (response.data === null) {
            return throwError(() => new Error('Requested page is not available!'));
          }

          return of(response);
        })
      );
  }

  getExcelVesselsItinerary(queryParams?: Params): Observable<{
    meta: Pagination;
    data: VesselItinerary[];
  }> {
    const params = {
      resource_type: 'simple',
      ...queryParams
    };

    return this.httpService.get<{
      meta: Pagination;
      data: VesselItinerary[];
    }>(`${ItineraryService.API}/excels?`, {
      params: new HttpParams({
        fromObject: params
      })
    });
  }

  getDetailMovement(id: string): Observable<{ data: VesselItinerary }> {
    const params = {
      resource_type: 'simple'
    };
    return this.httpService.get<{
      data: VesselItinerary;
    }>(`${ItineraryService.API}/${id}`, { params });
  }

  createMovement(movement: any): Observable<any> {
    return this.httpService.post(`${ItineraryService.API}`, movement);
  }

  updateMovement(id: string, body: any): Observable<any> {
    return this.httpService.put(`${ItineraryService.API}/${id}`, body, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded;'
      }
    });
  }

  deleteMovement(id: string | number) {
    return this.httpService.delete(`${ItineraryService.API}/${id}`, {});
  }

  getHfoMgoList(type: 1 | 2): Observable<{ data: HfoMgoBunker[] }> {
    return this.httpService.get<{ data: HfoMgoBunker[] }>(`${ItineraryService.ADMIN_API}/bunker_grades`, {
      params: {
        'filter[bunker_type]': type,
        limit: 100
      }
    });
  }

  updateMovementSequences(body: any): Observable<any> {
    return this.httpService.post(`${ItineraryService.API}/sequences`, body, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded;'
      }
    });
  }

  getDetailVessel(id: string): Observable<{ data: Vessel }> {
    return this.httpService.get<{ data: Vessel }>(`${ItineraryService.ADMIN_API}/vessels/${id}`, {});
  }

  getAdvanceFilters(key: string, limit?: number, queryParams?: Params): Observable<{ data: any[] }> {
    return this.httpService.get<{ data: any[] }>(`${ItineraryService.API}/advance_filters`, {
      params: {
        advance_filter: key,
        limit: limit ?? LIMIT_PER_PAGE_DEFAULT,
        ...queryParams
      }
    });
  }
}
